import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAID_TIERS = [
  { slug: "clip",  price_usd:  3000, length_hint: "1 minute",   blurb: "1 minute · 1 revision · MP3 · commercial license" },
  { slug: "song",  price_usd: 15000, length_hint: "3 minutes",  blurb: "3 minutes · 3 revisions · MP3 + WAV · plan editor · commercial license" },
  { slug: "EP",    price_usd: 30000, length_hint: "5 minutes",  blurb: "5 minutes · 5 revisions · stem separation · plan editor · broadcast rights" },
  { slug: "album", price_usd: 60000, length_hint: "15 minutes", blurb: "15 minutes · unlimited revisions · stems · video-to-music · film rights · optional artist signing" },
];

// Founder-set 2026-08-11 late-evening. Sage is the PREMIUM tier now; Resilience is
// no longer linked from this page (Resilience links here instead, per Founder ruling).
const STORE_KEY = "ms_pending_paid_release_v1";

/**
 * MusicStudioPage — the family Music Studio, public product.
 * Free: 30-second orchestral previews (anonymous).
 * Paid tiers (via Purser · PayPal Phase 1): Sketch / Scene / Act / Full-scale.
 */
export default function MusicStudioPage() {
  const [prompt, setPrompt] = useState(
    "A hopeful cinematic overture with sweeping strings, distant brass, and a rising choir at the climax."
  );
  const [composerName, setComposerName] = useState("");
  const [email, setEmail] = useState("");
  const [lengthSec, setLengthSec] = useState(30);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("");
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [status, setStatus] = useState(null);
  const [releaseNotice, setReleaseNotice] = useState(null);
  // Front 6 improvement · plain-ink certificate preview for album tier
  const [certPreviewText, setCertPreviewText] = useState("");

  const loadTiers = async () => {
    // Kept as a no-op — the price-drop clock was retired at the Founder's
    // word on 2026-08-12. Tier pricing is now purely static, no "N sales
    // this month before cut" copy.
  };

  const loadCertPreview = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/music-studio/certificate/preview`);
      if (r.ok) setCertPreviewText(await r.text());
    } catch {}
  };

  const loadGallery = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/music-studio/gallery?limit=12`);
      if (r.ok) {
        const d = await r.json();
        setGallery(d.compositions || []);
      }
    } catch {}
  };

  const loadStatus = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/music-studio/status`);
      if (r.ok) setStatus(await r.json());
    } catch {}
  };

  // On mount, look for a PayPal return (?ms_order=<paypal_order_id>&ms_tier=<tier>).
  // If found, walk capture → compose to release the full-length composition.
  useEffect(() => {
    loadGallery();
    loadStatus();
    loadTiers();
    loadCertPreview();
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("ms_order");
    const tierSlug = params.get("ms_tier");
    if (!orderId || !tierSlug) return;
    (async () => {
      try {
        setBusy(true);
        setBusyLabel("Capturing your payment…");
        // 1. Capture via Purser
        const capR = await fetch(`${BACKEND_URL}/api/purser/capture/${orderId}`, { method: "POST" });
        if (!capR.ok) {
          const d = await capR.json().catch(() => ({}));
          throw new Error(d.detail || `capture HTTP ${capR.status}`);
        }
        // 2. Pull pending draft (prompt/composer/email) from localStorage
        let draft = {};
        try { draft = JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch {}
        const savedPrompt = draft.prompt || prompt;
        setBusyLabel("Composing your full-length score…");
        // 3. Release via /compose
        const compR = await fetch(`${BACKEND_URL}/api/music-studio/compose`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prompt: savedPrompt,
            composer_name: draft.composerName || undefined,
            email: draft.email || undefined,
            tier: tierSlug,
            purser_id: orderId,
          }),
        });
        const d = await compR.json();
        if (!compR.ok) throw new Error(d.detail || `HTTP ${compR.status}`);
        setResult(d);
        setReleaseNotice(`Your ${tierSlug} tier composition is ready. ${d.seconds}s.`);
        loadGallery();
        loadStatus();
        try { localStorage.removeItem(STORE_KEY); } catch {}
        // Strip the query params so a refresh doesn't retry
        window.history.replaceState({}, "", "/studio");
      } catch (e) {
        setError(String(e.message || e));
      } finally {
        setBusy(false);
        setBusyLabel("");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const compose = async (e) => {
    e?.preventDefault?.();
    setError(null);
    setResult(null);
    if (prompt.trim().length < 8) {
      setError("Give the score a real sentence to grow from.");
      return;
    }
    setBusy(true);
    setBusyLabel("Composing your 30-second preview…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/music-studio/compose`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: prompt.trim(),
          composer_name: composerName.trim() || undefined,
          music_length_ms: Math.min(30, Math.max(10, parseInt(lengthSec || 30, 10))) * 1000,
          tier: "free",
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setResult(d);
      loadGallery();
      loadStatus();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
      setBusyLabel("");
    }
  };

  // Open a Purser checkout for a paid tier. Saves the draft to localStorage so
  // the return-from-PayPal handler can release the composition on the same page.
  const buyTier = async (tier) => {
    setError(null);
    if (prompt.trim().length < 8) {
      setError("Write the prompt before buying — the score is grown from it.");
      return;
    }
    setBusy(true);
    setBusyLabel(`Opening ${tier.slug} tier checkout…`);
    try {
      const base = window.location.origin;
      const return_url = `${base}/studio?ms_order={{ORDER}}&ms_tier=${tier.slug}`;
      const cancel_url = `${base}/studio?ms_cancel=1`;
      const r = await fetch(`${BACKEND_URL}/api/music-studio/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier: tier.slug,
          return_url,
          cancel_url,
          email: email.trim() || undefined,
          composer_name: composerName.trim() || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      // Persist the draft so the return handler can compose against it
      try {
        localStorage.setItem(
          STORE_KEY,
          JSON.stringify({
            prompt: prompt.trim(),
            composerName: composerName.trim(),
            email: email.trim(),
            tier: tier.slug,
            paypal_order_id: d.paypal_order_id,
          })
        );
      } catch {}
      // PayPal doesn't substitute {{ORDER}} — we rewrite to the actual order id
      const finalReturn = return_url.replace("{{ORDER}}", d.paypal_order_id);
      // Some environments strip the return_url param through — safer to just point the buyer at approve_url,
      // and rely on our stored order id + a plain "Release" fallback button.
      window.location.href = d.approve_url;
      // Note: some sandbox flows may not bounce back to return_url. If they don't,
      // the "Release my paid composition" section below lets the buyer paste the order id.
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
      setBusyLabel("");
    }
  };

  const manualRelease = async () => {
    const orderId = window.prompt("Paste the PayPal order id from your checkout receipt:");
    if (!orderId) return;
    const tierSlug = window.prompt("Which tier did you buy? (clip | song | EP | album)");
    if (!tierSlug) return;
    setBusy(true);
    setBusyLabel("Capturing + releasing your composition…");
    try {
      const capR = await fetch(`${BACKEND_URL}/api/purser/capture/${orderId}`, { method: "POST" });
      if (!capR.ok) {
        const d = await capR.json().catch(() => ({}));
        // If already captured, keep going
        if (!(d.detail || "").toLowerCase().includes("captured")) throw new Error(d.detail || `capture HTTP ${capR.status}`);
      }
      let draft = {};
      try { draft = JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch {}
      const savedPrompt = draft.prompt || prompt;
      const compR = await fetch(`${BACKEND_URL}/api/music-studio/compose`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: savedPrompt,
          composer_name: draft.composerName || composerName || undefined,
          email: draft.email || email || undefined,
          tier: tierSlug,
          purser_id: orderId,
        }),
      });
      const d = await compR.json();
      if (!compR.ok) throw new Error(d.detail || `HTTP ${compR.status}`);
      setResult(d);
      setReleaseNotice(`Your ${tierSlug} tier composition is ready. ${d.seconds}s.`);
      loadGallery();
      loadStatus();
      try { localStorage.removeItem(STORE_KEY); } catch {}
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
      setBusyLabel("");
    }
  };

  const audioSrc = result?.audio_base64
    ? `data:audio/mpeg;base64,${result.audio_base64}`
    : null;

  return (
    <div className="min-h-screen bg-sage-bg text-sage-ink">
      <TopNav />
      <div className="max-w-5xl mx-auto px-6 pt-12 pb-24">
        <header className="mb-10" data-testid="music-studio-hero">
          <p className="text-sm uppercase tracking-widest text-sage-accent mb-2">Sage Music Studio</p>
          <div className="flex items-start gap-4 mb-4">
            <div className="text-sage-accent shrink-0"><ProductMark slug="choir" size={56} /></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-tight">
              Compose orchestra-grade music from a sentence.
            </h1>
          </div>
          <p className="text-base sm:text-lg text-sage-ink/80 max-w-2xl">
            Type what the score should <em>do</em> — the feeling, the frame, the instruments —
            and the family's own composer hands you a real orchestral piece.
            No stock loops. No AI-tinny synth. Real orchestra grade.
          </p>
          {status && (
            <p className="text-xs text-sage-ink/60 mt-4 tabular-nums" data-testid="music-studio-status">
              {status.compositions_baked} compositions baked · engine: {status.engine} · phase {status.phase.replace(/^./, s=>s.toUpperCase())}
            </p>
          )}
        </header>

        <form onSubmit={compose} className="mb-10 space-y-4" data-testid="music-studio-form">
          <label className="block">
            <span className="block text-sm font-semibold mb-2">Prompt</span>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="w-full min-h-[140px] rounded-lg border border-sage-line bg-white px-4 py-3 text-base leading-relaxed focus:outline-none focus:border-sage-accent"
              placeholder="An orchestral overture, hopeful, sweeping…"
              maxLength={2000}
              data-testid="music-studio-prompt-input"
              disabled={busy}
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-sm font-semibold mb-2">Composer name (optional)</span>
              <input
                value={composerName}
                onChange={(e) => setComposerName(e.target.value)}
                className="w-full rounded-lg border border-sage-line bg-white px-4 py-2.5 text-base focus:outline-none focus:border-sage-accent"
                placeholder="anonymous"
                maxLength={80}
                data-testid="music-studio-composer-input"
                disabled={busy}
              />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold mb-2">Length (seconds · 10–30 free)</span>
              <input
                type="number"
                min={10} max={30}
                value={lengthSec}
                onChange={(e) => setLengthSec(e.target.value)}
                className="w-full rounded-lg border border-sage-line bg-white px-4 py-2.5 text-base tabular-nums focus:outline-none focus:border-sage-accent"
                data-testid="music-studio-length-input"
                disabled={busy}
              />
            </label>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={busy || !prompt.trim()}
                className="w-full rounded-lg bg-sage-ink text-sage-bg font-semibold py-3 px-5 hover:bg-sage-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="music-studio-compose-button"
              >
                {busy ? "Composing…" : "Compose the score"}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="music-studio-error">
              {error}
            </div>
          )}
          {busy && busyLabel && (
            <div className="rounded-lg border border-sage-line bg-sage-elevated px-4 py-3 text-sm" data-testid="music-studio-busy">
              {busyLabel}
            </div>
          )}
          {releaseNotice && (
            <div className="rounded-lg border border-sage-accent bg-white px-4 py-3 text-sm" data-testid="music-studio-release-notice">
              {releaseNotice}
            </div>
          )}
        </form>

        <section className="mb-14" data-testid="music-studio-tiers">
          <div className="mb-6">
            <p className="text-sm uppercase tracking-widest text-sage-accent mb-2">Sage · premium · one-time · buyer keeps the file</p>
            <h2 className="text-2xl sm:text-3xl font-serif leading-tight mb-2">
              Buy the full-length composition here — the ethical, expensive way.
            </h2>
            <p className="text-sm sm:text-base text-sage-ink/80 max-w-2xl">
              Every dollar on this page tips a working artist. Optional artist signing at the album tier — we produce you, not just for you. No royalty tail. No re-use fee. No gatekeeper.
            </p>
          </div>
          <label className="block mb-4 max-w-md">
            <span className="block text-sm font-semibold mb-2">Email (for the delivery receipt · optional)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-sage-line bg-white px-4 py-2.5 text-base focus:outline-none focus:border-sage-accent"
              placeholder="you@example.com"
              maxLength={200}
              data-testid="music-studio-email-input"
              disabled={busy}
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PAID_TIERS.map((tier) => (
              <div key={tier.slug} className="rounded-xl border border-sage-line bg-white p-5 flex flex-col" data-testid={`music-studio-tier-card-${tier.slug}`}>
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-xl font-serif">{tier.slug === "EP" ? "EP" : tier.slug}</h3>
                  <span className="text-2xl tabular-nums font-semibold">${tier.price_usd.toLocaleString()}</span>
                </div>
                <p className="text-xs uppercase tracking-widest text-sage-ink/60 mb-2">{tier.length_hint} ceiling</p>
                <p className="text-sm text-sage-ink/80 mb-4 flex-1">{tier.blurb}</p>
                {tier.slug === "album" && (
                  <details className="mb-3" data-testid="cert-preview-reveal">
                    <summary className="text-xs uppercase tracking-wider text-sage-accent cursor-pointer hover:text-sage-ink">
                      Preview the SOLE COMPOSER certificate ↓
                    </summary>
                    <pre className="mt-2 text-[10px] leading-relaxed bg-sage-elevated border border-sage-line p-3 rounded whitespace-pre-wrap text-sage-ink/80 max-h-64 overflow-y-auto" data-testid="cert-preview-text">
{certPreviewText || "Loading preview…"}
                    </pre>
                  </details>
                )}
                <button
                  type="button"
                  onClick={() => buyTier(tier)}
                  disabled={busy || !prompt.trim()}
                  className="w-full rounded-lg bg-sage-ink text-sage-bg font-semibold py-2.5 px-4 hover:bg-sage-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`music-studio-buy-${tier.slug}`}
                >
                  Order the {tier.slug === "EP" ? "EP" : tier.slug} tier
                </button>
              </div>
            ))}
          </div>
          <div className="mt-6 text-xs text-sage-ink/60">
            After payment approves, this page auto-releases your composition. If your browser doesn't return here,{" "}
            <button
              type="button"
              onClick={manualRelease}
              className="underline text-sage-accent hover:text-sage-ink"
              data-testid="music-studio-manual-release"
              disabled={busy}
            >
              paste your order id to release it manually
            </button>.
          </div>
        </section>

        {result && (
          <section className="mb-14 rounded-xl border-2 border-sage-accent bg-white p-6" data-testid="music-studio-result">
            <p className="text-xs uppercase tracking-widest text-sage-accent mb-2">Your score</p>
            <h2 className="text-2xl font-serif mb-4">{result.seconds}-second orchestral composition</h2>
            <audio controls className="w-full mb-3" data-testid="music-studio-audio-player">
              <source src={audioSrc} type="audio/mpeg" />
              Your browser doesn't support audio playback.
            </audio>
            <div className="flex flex-wrap gap-3 text-sm text-sage-ink/70">
              <a
                href={audioSrc}
                download={`sage-music-studio-${result.comp_id}.mp3`}
                className="rounded-md border border-sage-line px-3 py-1.5 hover:bg-sage-line/20"
                data-testid="music-studio-download-link"
              >
                Download MP3
              </a>
              <a
                href={`/api/music-studio/download/${result.comp_id}`}
                className="rounded-md border border-sage-line px-3 py-1.5 hover:bg-sage-line/20"
              >
                Permanent link
              </a>
              <span className="self-center tabular-nums text-xs opacity-70">
                comp {result.comp_id.slice(0, 8)}
              </span>
            </div>
          </section>
        )}

        {gallery.length > 0 && (
          <section data-testid="music-studio-gallery">
            <h2 className="text-2xl font-serif mb-4">Recent compositions</h2>
            <ul className="space-y-4">
              {gallery.map((c) => (
                <li key={c.comp_id} className="rounded-lg border border-sage-line bg-white/60 p-4">
                  <p className="text-xs uppercase tracking-widest text-sage-ink/60 mb-1">
                    {c.composer_name || "anonymous"} · {c.seconds}s
                  </p>
                  <p className="text-sm leading-relaxed">{c.prompt}</p>
                  <a
                    href={`/api/music-studio/download/${c.comp_id}`}
                    className="inline-block mt-2 text-xs underline text-sage-accent"
                  >
                    Listen ↓
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
